import { Link } from 'react-router-dom';
import { Truck, Clock, MapPin, Package, CheckCircle2, ArrowRight } from 'lucide-react';

export default function Delivery() {
  return (
    <div className="pt-20 min-h-screen bg-cream" data-testid="delivery-page">
      {/* Header */}
      <section className="py-16 bg-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.pexels.com/photos/3978831/pexels-photo-3978831.jpeg?w=1920"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-cream mb-4" data-testid="delivery-title">
            Delivery Information
          </h1>
          <p className="font-manrope text-cream/70 text-lg max-w-xl">
            Fresh from the farm to your door \u2014 here's everything you need to know.
          </p>
        </div>
      </section>

      {/* Main Info */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {[
              {
                icon: MapPin,
                title: 'Delivery Area',
                description: 'All ZIP codes in the White Mountain Region, NH'
              },
              {
                icon: Clock,
                title: 'Delivery Days',
                description: 'Tuesday, Thursday, Saturday'
              },
              {
                icon: Truck,
                title: 'Delivery Fee',
                description: '$4.99 (Free over $50)'
              },
              {
                icon: Package,
                title: 'Packaging',
                description: 'Insulated, eco-friendly boxes'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 border border-forest/10 text-center">
                <div className="w-14 h-14 bg-forest/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-forest" />
                </div>
                <h3 className="font-playfair text-xl font-bold text-dark mb-2">{item.title}</h3>
                <p className="font-manrope text-dark/60">{item.description}</p>
              </div>
            ))}
          </div>

          {/* Detailed Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* How It Works */}
            <div className="bg-white rounded-2xl p-8 border border-forest/10">
              <h2 className="font-playfair text-2xl font-bold text-dark mb-6">How Delivery Works</h2>
              <div className="space-y-6">
                {[
                  {
                    step: '01',
                    title: 'Order by midnight',
                    desc: 'Place your order by midnight the day before your delivery day.'
                  },
                  {
                    step: '02',
                    title: 'We harvest & pack',
                    desc: 'Your items are picked fresh and carefully packed in insulated boxes.'
                  },
                  {
                    step: '03',
                    title: 'Delivered to you',
                    desc: 'Your box arrives at your door, staying fresh for hours.'
                  },
                  {
                    step: '04',
                    title: 'Enjoy!',
                    desc: 'Unpack and enjoy the freshest local food in the region.'
                  }
                ].map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <span className="font-caveat text-3xl text-gold">{item.step}</span>
                    <div>
                      <h4 className="font-manrope font-bold text-dark mb-1">{item.title}</h4>
                      <p className="font-manrope text-dark/60 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* What to Know */}
            <div className="bg-white rounded-2xl p-8 border border-forest/10">
              <h2 className="font-playfair text-2xl font-bold text-dark mb-6">Good to Know</h2>
              <div className="space-y-4">
                {[
                  'You don\'t need to be home \u2014 we\'ll leave your box in a safe spot',
                  'Boxes stay fresh for several hours after delivery',
                  'Skip or reschedule deliveries anytime via your dashboard',
                  'All packaging is recyclable or compostable',
                  'Refrigerate perishables within 4 hours of delivery',
                  'Contact us within 24 hours if anything isn\'t perfect'
                ].map((item, index) => (
                  <div key={index} className="flex gap-3">
                    <CheckCircle2 className="w-5 h-5 text-forest flex-shrink-0 mt-0.5" />
                    <p className="font-manrope text-dark/70">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Map Placeholder */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-playfair text-3xl font-bold text-dark mb-4">Delivery Coverage</h2>
            <p className="font-manrope text-dark/70">
              We proudly serve the entire White Mountain Region of New Hampshire.
            </p>
          </div>

          <div className="bg-cream rounded-2xl p-8 border border-forest/10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                'Franconia', 'Lincoln', 'Conway', 'Gorham',
                'Berlin', 'Littleton', 'Woodstock', 'Jackson',
                'Bethlehem', 'Sugar Hill', 'Bartlett', 'And more...'
              ].map((town, index) => (
                <div key={index} className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-forest" />
                  <span className="font-manrope text-dark">{town}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-forest">
        <div className="container-custom text-center">
          <h2 className="font-playfair text-4xl font-bold text-cream mb-6">
            Ready for Farm-Fresh Delivery?
          </h2>
          <p className="font-manrope text-cream/80 text-lg mb-8 max-w-xl mx-auto">
            Start your subscription today and taste the difference of truly local food.
          </p>
          <Link to="/shop" className="btn-accent" data-testid="delivery-shop-btn">
            Start Shopping
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
}
